import { Truck, CheckCircle, Clock, MapPin, ArrowRight, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const VehicleTransportPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Vehicle Transportation Hackney | Car Transport Service East London"
        description="Professional vehicle transportation in Hackney & East London. Running & non-running cars. Door-to-door service. Call 07492 639223 for secure car transport."
        keywords="vehicle transportation hackney, car transport east london, vehicle delivery hackney, car transport service, non-running car transport, vehicle delivery service london"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🚛 Professional Vehicle Transport
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Vehicle Transportation Service in Hackney & East London
            </h1>
            <p className="text-xl text-slate-200 mb-8">
              Secure car transport for running and non-running vehicles across Hackney, East London. Professional door-to-door service within 20-mile radius. Fully insured transportation for all vehicle types.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900">
                  Get Transport Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Professional Vehicle Transportation in Hackney</h2>
            <p className="text-lg text-slate-700 mb-6">
              Ovawell Autos provides professional vehicle transportation services across Hackney and East London. Whether you need to move a running car, transport a non-running vehicle, or deliver a newly purchased car, we offer secure, insured door-to-door service within our 20-mile coverage area.
            </p>

            <h3 className="text-2xl font-bold mb-4 mt-8">What We Transport</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Running vehicles (all makes and models)",
                "Non-running cars (mechanical issues)",
                "Vehicles that won't start",
                "Cars that can't be driven (no MOT/insurance)",
                "Project cars to mechanics",
                "Newly purchased vehicles",
                "Cars for storage or relocation",
                "Classic and modified cars",
                "Small vans and 4x4s",
                "Vehicles with body damage",
                "Cars from mechanics to home",
                "Auction purchased vehicles"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Service Areas - Hackney & 20 Miles</h3>
            <div className="grid md:grid-cols-3 gap-3 mb-8">
              {[
                "Hackney Central",
                "Dalston",
                "Shoreditch",
                "Hoxton",
                "Bethnal Green",
                "Bow",
                "Mile End",
                "Stratford",
                "Leyton",
                "Walthamstow",
                "Islington",
                "Tower Hamlets"
              ].map((area, idx) => (
                <div key={idx} className="bg-white p-3 rounded border flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <span className="text-sm">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold mb-4 mt-8">Vehicle Transport Prices</h3>
            <Card className="mb-8">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between pb-3 border-b">
                    <span>Within Hackney</span>
                    <span className="font-bold">£60-£80</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b">
                    <span>Hackney to Stratford/Islington</span>
                    <span className="font-bold">£70-£90</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b">
                    <span>Up to 10 miles</span>
                    <span className="font-bold">£80-£110</span>
                  </div>
                  <div className="flex justify-between pb-3 border-b">
                    <span>10-20 miles</span>
                    <span className="font-bold">£110-£140</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Non-running vehicle surcharge</span>
                    <span className="font-bold">+£20-£40</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h3 className="text-2xl font-bold mb-4 mt-8">Why Choose Our Vehicle Transport Service?</h3>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <Truck className="w-8 h-8 text-blue-600 mb-3" />
                  <h4 className="font-bold mb-2">Professional Equipment</h4>
                  <p className="text-slate-600">Modern recovery vehicles with secure tie-downs, wheel dollies for immobile cars, and equipment for all vehicle types.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Package className="w-8 h-8 text-blue-600 mb-3" />
                  <h4 className="font-bold mb-2">Fully Insured</h4>
                  <p className="text-slate-600">Comprehensive goods in transit insurance covers your vehicle throughout collection, transport, and delivery.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <Clock className="w-8 h-8 text-blue-600 mb-3" />
                  <h4 className="font-bold mb-2">Same-Day Service Available</h4>
                  <p className="text-slate-600">Book in advance or request same-day collection and delivery across Hackney and East London.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <CheckCircle className="w-8 h-8 text-blue-600 mb-3" />
                  <h4 className="font-bold mb-2">Door-to-Door</h4>
                  <p className="text-slate-600">Collection from any address in Hackney or East London, delivery directly to your destination.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-blue-900 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Vehicle Transportation?</h2>
          <p className="text-xl mb-8">Professional car transport service across Hackney & East London</p>
          <a href={`tel:${companyInfo.phone}`}>
            <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
              Call {companyInfo.phone}
            </Button>
          </a>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
